// Client error reporter (docs/plan/12-frontend.md section 11). Global `error` and
// `unhandledrejection` handlers and the error boundaries post a small, capped, anonymous report to
// POST /api/v1/client-errors. Nothing here ever reads user data: only the error name, message and
// stack, the route path (no query string, no fragment) and the build id are sent.
import { ApiError, type ApiFetcher } from '@batiflow/client-core';
import { ClientErrorReportSchema, type ClientErrorReport } from '@batiflow/contracts';

export type ReportKind = ClientErrorReport['kind'];

export interface ErrorReporterOptions {
  fetcher: ApiFetcher;
  release: string;
  /** Current location path (pathname only is kept). */
  getPath: () => string;
  now?: () => number;
  /** Maximum reports per window (default 5 per minute). */
  maxReports?: number;
  windowMs?: number;
}

export interface ErrorReporter {
  report(kind: ReportKind, error: unknown, extra?: { componentStack?: string | undefined }): void;
  /** Registers the window handlers; returns the function that removes them. */
  install(target: Pick<Window, 'addEventListener' | 'removeEventListener'>): () => void;
}

/** Keeps only the pathname: strips any query string or fragment, and guarantees a leading "/". */
export function pathOnly(path: string): string {
  const clean = path.split(/[?#]/, 1)[0] ?? '';
  const withSlash = clean.startsWith('/') ? clean : `/${clean}`;
  return withSlash.slice(0, 300);
}

function describe(error: unknown): { name: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message,
      ...(error.stack ? { stack: error.stack } : {}),
    };
  }
  if (typeof error === 'string') return { name: 'Error', message: error };
  return { name: 'NonError', message: 'A non-error value was thrown' };
}

export function createErrorReporter(options: ErrorReporterOptions): ErrorReporter {
  const now = options.now ?? (() => Date.now());
  const maxReports = options.maxReports ?? 5;
  const windowMs = options.windowMs ?? 60_000;
  let sent: number[] = [];

  const allowed = (): boolean => {
    const current = now();
    sent = sent.filter((at) => current - at < windowMs);
    if (sent.length >= maxReports) return false;
    sent.push(current);
    return true;
  };

  const report: ErrorReporter['report'] = (kind, error, extra) => {
    const info = describe(error);
    const candidate: Record<string, unknown> = {
      kind,
      name: info.name.slice(0, 100),
      message: info.message.slice(0, 500),
      route: pathOnly(options.getPath()),
      release: options.release.slice(0, 50),
      occurredAt: new Date(now()).toISOString(),
    };
    if (info.stack) candidate.stack = info.stack.slice(0, 4000);
    if (extra?.componentStack) candidate.componentStack = extra.componentStack.slice(0, 4000);
    if (error instanceof ApiError && error.traceId) candidate.traceId = error.traceId.slice(0, 64);

    const parsed = ClientErrorReportSchema.safeParse(candidate);
    if (!parsed.success || !allowed()) return;
    void send(options.fetcher, parsed.data);
  };

  return {
    report,
    install(target) {
      const onError = (event: Event) => {
        const e = event as ErrorEvent;
        report('error', e.error ?? e.message);
      };
      const onRejection = (event: Event) => {
        report('unhandledrejection', (event as PromiseRejectionEvent).reason);
      };
      target.addEventListener('error', onError);
      target.addEventListener('unhandledrejection', onRejection);
      return () => {
        target.removeEventListener('error', onError);
        target.removeEventListener('unhandledrejection', onRejection);
      };
    },
  };
}

async function send(fetcher: ApiFetcher, body: ClientErrorReport): Promise<void> {
  try {
    await fetcher({ method: 'POST', url: '/api/v1/client-errors', data: body });
  } catch {
    // Best effort: the endpoint may be down or rate limited. Never report a reporting failure.
  }
}
